/**
 * Finding faces in a photo, on the phone.
 *
 * MediaPipe's face detector (the full-range model, which finds people in a
 * group as well as a face filling a selfie), served from the app's own site
 * under /mediapipe/ (vite.config.ts). It is a plain script that puts
 * `FaceDetection` on the window, loaded the first time it is wanted; nothing
 * about the photo leaves the phone.
 */

/** A point on the photo, as fractions of its width and height. */
export interface Spot {
  x: number;
  y: number;
}

/** One face: its eyes as seen (left of the picture first), nose and mouth. */
export interface Face {
  leftEye: Spot;
  rightEye: Spot;
  nose: Spot;
  mouth: Spot;
}

interface Detection {
  landmarks: Spot[];
}
interface Detector {
  setOptions(options: Record<string, unknown>): void;
  onResults(listener: (results: { detections: Detection[] }) => void): void;
  initialize(): Promise<void>;
  send(input: { image: HTMLImageElement | HTMLCanvasElement }): Promise<void>;
}
type DetectorClass = new (config: { locateFile: (file: string) => string }) => Detector;

export const FACE_FINDER_ROOT = '/mediapipe/';
export const FACE_CONFIDENCE = 0.35;

let ready: Promise<Detector> | null = null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('The face finder did not load.'));
    document.head.appendChild(script);
  });
}

/** The detector, loaded once and kept; a failed load is tried afresh next time. */
function detector(): Promise<Detector> {
  ready ??= (async () => {
    const win = window as unknown as { FaceDetection?: DetectorClass };
    if (!win.FaceDetection) await loadScript(`${FACE_FINDER_ROOT}face_detection.js`);
    if (!win.FaceDetection) throw new Error('The face finder did not load.');
    const found = new win.FaceDetection({ locateFile: (file) => `${FACE_FINDER_ROOT}${file}` });
    // 0.35, tried on the family's own photos: at the default 0.5 it found two
    // of four people on a bench; at 0.25 it found all four and a hand besides.
    found.setOptions({ model: 'full', minDetectionConfidence: FACE_CONFIDENCE });
    await found.initialize();
    return found;
  })().catch((err) => {
    ready = null;
    throw err;
  });
  return ready;
}

/**
 * Every face in the photo. The detector's six points are, in order: the
 * person's right eye (on the left of the picture), their left eye, the tip of
 * the nose, the middle of the mouth, and the two ears.
 */
export async function findFaces(image: HTMLImageElement): Promise<Face[]> {
  const found = await detector();
  const results = new Promise<{ detections: Detection[] }>((resolve) => found.onResults(resolve));
  await found.send({ image });
  const { detections } = await results;
  return detections
    .filter((detection) => detection.landmarks.length >= 4)
    .map(({ landmarks: [a, b, nose, mouth] }) => ({
      leftEye: a!.x <= b!.x ? a! : b!,
      rightEye: a!.x <= b!.x ? b! : a!,
      nose: nose!,
      mouth: mouth!,
    }));
}

/** For tests: forget the loaded detector. */
export function forgetFaceFinder(): void {
  ready = null;
}
